import { roundUpDecimal } from "@/helpers/numberHelpers";

/**
 * Formats a number into a localized string.
 *
 * - If `type` is `"integer"`, the value is formatted with no decimals, not rounded.
 * - If `type` is `"decimal"`, the value is rounded **up** to 3 decimal places.
 * - The default locale is `"en-US"` but can be customized.
 *
 * @param value - The numeric value to format.
 * @param type - Formatting type: "integer" (default) or "decimal".
 * @param locale - Optional locale string (default: "en-US").
 * @returns A formatted string or original value if invalid.
 */
export const formatNumber = (
  value: number,
  type: "integer" | "decimal" = "integer",
  locale: string = "en-US",
  minimumFractionDigits: number = 0,
  maximumFractionDigits: number = 3,
  isRoundUp: boolean = true
): string | number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return value;
  }

  const isInteger = type === "integer";
  const displayValue =
    isInteger || !isRoundUp
      ? value
      : roundUpDecimal(value, maximumFractionDigits);

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: isInteger ? 0 : maximumFractionDigits
  }).format(displayValue);
};
